<template>
  <b-overlay :show="loading">
    <b-form @submit.prevent="onSubmit()" data-vv-scope="activity-form">
      <b-card>
        <b-card-title class="mt-3 mb-75">
          {{ activity.id ? 'Editar' : 'Nueva' }} Actividad
        </b-card-title>
        <b-form-group
          label="Nombre"
          label-for="name"
          class="mb-3"
        >
          <b-form-input
            id="name"
            placeholder="Nombre"
            name="name"
            v-model="activity.name"
            :state="errors.has('activity-form.name') ? false : null"
            v-validate="'required'"
          />
          <b-form-invalid-feedback>
            {{ errors.first('activity-form.name') }}
          </b-form-invalid-feedback>
        </b-form-group>
        <b-form-checkbox
          class="custom-control-info float-right mb-2"
          name="check-button"
          switch
          v-model="showDescriptionPreview"
        >
          <span class="switch-icon-left">
            <feather-icon icon="TypeIcon" />
          </span>
          <span class="switch-icon-right">
            <feather-icon icon="PenToolIcon" />
          </span>
        </b-form-checkbox>
        <b-form-group
          label="Descripción"
          label-for="description"
          class="mb-3"
        >
          <b-form-textarea
            id="description"
            name="description"
            placeholder="Descripción"
            rows="4"
            v-model="activity.description"
            :state="errors.has('activity-form.description') ? false : null"
            v-validate="'required'"
          />
          <b-form-invalid-feedback>
            {{ errors.first('activity-form.description') }}
          </b-form-invalid-feedback>
        </b-form-group>
        <div
          v-if="showDescriptionPreview"
          class="border markdown rounded mb-3 px-4 py-3"
          :inner-html.prop="activity.description | markdown"
        />
        <ListUser
          label="Usuario asignado"
          name="targetUserId"
          class="mb-3"
          data-vv-as="usuario asignado"
          v-model="activity.targetUserId"
          :danger="errors.has('activity-form.targetUserId')"
          :danger-text="errors.first('activity-form.targetUserId')"
          v-validate="'required'"
        />
        <ListSystemActivityStatus
          v-if="editMode"
          label="Estado de sistema"
          name="statusValue"
          class="mb-3"
          v-model="activity.statusValue"
          :danger="errors.has('activity-form.statusValue')"
          :danger-text="errors.first('activity-form.statusValue')"
          v-validate="'required'"
          ref="listSystemActivityStatus"
        />
        <ListCompanyActivityStatus
          :parent-value="activity.statusValue"
          label="Estado de compañia"
          name="companyStatusValue"
          class="mb-3"
          data-vv-as="estado de compañía"
          v-model="activity.companyStatusValue"
          :danger="errors.has('activity-form.companyStatusValue')"
          :danger-text="errors.first('activity-form.companyStatusValue')"
          v-validate="'required'"
          ref="listCompanyActivityStatus"
        />
        <b-form-group
          label="Fecha de Vencimiento"
          label-for="date"
          class="mb-3"
        >
          <b-form-datepicker
            name="date"
            v-model="activity.expiredAt"
            data-vv-as="fecha de vencimiento"
            :min="minDate"
            :state="errors.has('activity-form.date') ? false : null"
            v-validate="'required'"
            label-help="Puede usar las flechas del teclado y espacio"
          />
          <b-form-invalid-feedback>
            {{ errors.first('activity-form.date') }}
          </b-form-invalid-feedback>
        </b-form-group>
        <div class="text-right">
          <b-button
            type="submit"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            variant="primary"
          >
            {{ editMode ? 'Editar' : 'Crear' }}
          </b-button>
        </div>
      </b-card>
    </b-form>
  </b-overlay>
</template>
<script>

import { mapActions } from 'vuex';

import ListUser from '@/febos/chile/lists/components/ListUser';
import ListSystemActivityStatus from '@/febos/chile/lists/components/ListSystemActivityStatus';
import ListCompanyActivityStatus from '@/febos/chile/lists/components/ListCompanyActivityStatus';
import { createActivity, updateActivity } from '@/febos/global/kanban/store/actions';

export default {
  data() {
    return {
      minDate: this.$moment().format('YYYY-MM-DD'),
      showDescriptionPreview: false,
      activity: {
        ...this.selectedActivity,
        statusValue: this.column?.value || this.selectedActivity.statusValue
      },
      loading: false
    };
  },
  methods: {
    ...mapActions('kanban', {
      createActivity: createActivity.name,
      updateActivity: updateActivity.name
    }),
    ...mapActions('Modals', [
      'closeModal'
    ]),
    async attemptCreate() {
      const { label: companyStatusName } = this.$refs.listCompanyActivityStatus.getOption();
      if (this.fromProcessedFile) {
        this.closeModal();
      }

      await this.createActivity({
        activity: {
          ...this.activity,
          ...(companyStatusName && { companyStatusName }),
          statusValue: this.column.value,
          statusName: this.column.name,
          referenceId: this.referenceId,
          referenceType: this.referenceType,
          referenceElementType: this.referenceType,
          referenceElementNumber: this.referenceNumber
        },
        kanbanId: this.kanbanId
      });
      this.activity = {
        statusValue: this.column.value
      };
      this.onCreated();
    },
    async attemptUpdate() {
      const { label: statusName } = this.$refs.listSystemActivityStatus.getOption() || {};
      const { label: companyStatusName } = this.$refs.listCompanyActivityStatus.getOption() || {};
      await this.updateActivity({
        activityId: this.activity.id,
        activity: {
          ...this.activity,
          ...(statusName && { statusName }),
          ...(companyStatusName && { companyStatusName }),
          ...(this.referenceType && { referenceElementType: this.referenceType }),
          ...(this.referenceNumber && { referenceElementNumber: this.referenceNumber })
        },
        kanbanId: this.kanbanId
      });
      this.onUpdated();
    },
    async onSubmit() {
      const result = await this.$validator.validateAll('activity-form');
      if (!result) {
        return;
      }
      this.loading = true;
      await this[this.editMode ? 'attemptUpdate' : 'attemptCreate']();
      this.$bvToast.toast(
        `Actividad ${this.editMode ? 'Actualizada' : 'Creada'}`,
        {
          title: 'Tablero',
          variant: 'primary',
          pauseOnHover: true,
          solid: true,
          noAutoHide: true
        }
      );
      await this.reset();
    },
    async reset() {
      await this.$validator.reset();
      this.loading = false;
    }
  },
  components: {
    ListUser,
    ListSystemActivityStatus,
    ListCompanyActivityStatus
  },
  props: {
    selectedActivity: { type: Object, default: () => {} },
    column: {
      value: { type: String, required: true },
      name: { type: Number, required: true }
    },
    kanbanId: { type: String, required: true },
    referenceId: String,
    referenceType: String,
    referenceNumber: String,
    editMode: { type: Boolean, default: false },
    onUpdated: { type: Function, default: () => {} },
    onCreated: { type: Function, default: () => {} },
    fromProcessedFile: { type: Boolean, default: false }
  }
};

</script>
